import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import { FieldError, FieldTitle } from '~/styles/forms/fieldFeedback';
import { FormField, StyledInput } from '~/styles/forms/formElements';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';
import type { LanguageCode, StudentType } from '~/types/congregationGroupTypes';
import { localeToApiLanguage } from '~/utils/languageFormat';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/campaign/add',
)({
  component: RouteComponent,
});

const STUDENT_TYPE_TO_API: Record<
  StudentType,
  components['schemas']['StudentType']
> = {
  all: 'ALL',
  postal: 'POSTAL',
  online: 'ONLINE',
};

/** Normalize free text into the lowercase URL-safe handle the backend accepts. */
function toCampaignHandle(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The host part of a custom domain, tolerating a bare host or a full URL. */
function hostOf(value: string): string {
  const trimmed = value.trim().replace(/^https?:\/\//, '');
  return trimmed.split('/')[0] ?? '';
}

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/campaign/add',
  });

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [subdomainError, setSubdomainError] = useState(false);
  const [subdomainErrorText, setSubdomainErrorText] = useState('');
  const [language, setLanguage] = useState<LanguageCode>('en');
  const languageLabels = useLanguageLabels();
  const [studentType, setStudentType] = useState<StudentType>('all');
  const [useCustomDomain, setUseCustomDomain] = useState(false);
  const [subdomain, setSubdomain] = useState('');

  const { data: group } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const { mutateAsync: createCampaign } = api.createCampaign.useMutation();

  const handleBack = () => {
    navigate({
      to: '/groups/$groupName/campaign/',
      params: { groupName },
    });
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    let hasError = false;
    if (!name.trim()) {
      setNameError(true);
      hasError = true;
    }
    if (!subdomain.trim()) {
      setSubdomainError(true);
      setSubdomainErrorText(t('wbs.this-field-is-required'));
      hasError = true;
    }
    if (hasError || !group) return;

    // In custom-domain mode the input is a whole URL, but a campaign still
    // needs its short handle (that's what the sign-up link carries), so derive
    // one from the host. In subdomain mode the input IS the handle.
    const handle = toCampaignHandle(
      useCustomDomain ? hostOf(subdomain) : subdomain,
    );
    if (!handle) {
      setSubdomainError(true);
      setSubdomainErrorText(t('wbs.this-field-is-required'));
      return;
    }

    try {
      await createCampaign({
        path: { group_id: group.id },
        body: {
          campaign_name: name.trim(),
          domain: handle,
          language: localeToApiLanguage(language),
          student_type: STUDENT_TYPE_TO_API[studentType],
          ...(useCustomDomain ? { url: subdomain.trim() } : {}),
        },
      });
    } catch {
      // The only field-level failure the backend reports is a taken handle.
      setSubdomainError(true);
      // Translation Needed
      setSubdomainErrorText('That campaign URL is already in use');
      return;
    }

    await api.listGroupCampaigns.invalidateQueries();
    navigate({
      to: '/groups/$groupName/campaign/',
      params: { groupName },
    });
  };

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('sidebar.campaigns')} />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        <form onSubmit={handleSubmit}>
          <FormField>
            <FieldTitle htmlFor="name" $hasError={nameError}>
              {t('wbs.name')}
            </FieldTitle>
            <StyledInput
              id="name"
              type="text"
              style={{ width: '100%' }}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim()) setNameError(false);
              }}
            />
            {nameError && (
              <FieldError>{t('wbs.this-field-is-required')}</FieldError>
            )}
          </FormField>

          <FormField>
            <FieldTitle htmlFor="language">{t('wbs.languages')}</FieldTitle>
            <SelectGroup>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value as LanguageCode)}
                style={{ color: 'var(--grey-900)' }}
              >
                <option value="en">{languageLabels.en}</option>
                <option value="es">{languageLabels.es}</option>
                <option value="fr">{languageLabels.fr}</option>
                <option value="pt-br">{languageLabels['pt-br']}</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            {/* Translation Needed */}
            <FieldTitle htmlFor="studentType">User Type</FieldTitle>
            <SelectGroup>
              <select
                id="studentType"
                value={studentType}
                style={{ color: 'var(--grey-900)' }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === 'all' ||
                    value === 'postal' ||
                    value === 'online'
                  ) {
                    setStudentType(value);
                  }
                }}
              >
                <option value="all">{t('lessons.all-students')}</option>
                {/* Translations Needed */}
                <option value="postal">Postal Students Only</option>
                <option value="online">Online Students Only</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <CheckboxLabel htmlFor="useCustomDomain">
              <WBSCheckbox
                id="useCustomDomain"
                checked={useCustomDomain}
                onChange={(e) => setUseCustomDomain(e.target.checked)}
              />
              {t('wbs.use-custom-domain')}
            </CheckboxLabel>
          </FormField>

          <FormField>
            <FieldTitle htmlFor="domainInput" $hasError={subdomainError}>
              {useCustomDomain ? t('wbs.custom-url') : t('wbs.subdomain')}
            </FieldTitle>
            {useCustomDomain ? (
              <StyledInput
                id="domainInput"
                type="text"
                value={subdomain}
                onChange={(e) => {
                  setSubdomain(e.target.value);
                  if (e.target.value.trim()) setSubdomainError(false);
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <SubdomainRow>
                <StyledInput
                  id="domainInput"
                  type="text"
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomain(e.target.value);
                    if (e.target.value.trim()) setSubdomainError(false);
                  }}
                  style={{ width: '250px' }}
                />
                {/* Translation Needed? */}
                <SubdomainSuffix>.worldbibleschool.org</SubdomainSuffix>
              </SubdomainRow>
            )}
            {subdomainError && <FieldError>{subdomainErrorText}</FieldError>}
          </FormField>

          <WBSButton wide type="submit">
            {t('actions.submit')}
          </WBSButton>
        </form>
      </FormContainer>
    </>
  );
}

const SubdomainRow = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
`;

const SubdomainSuffix = styled.span`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 16px;
  white-space: nowrap;
`;
